import React from "react";

function formatPairs(items, separator = " :: ") {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const values = [
        item?.title || item?.label || item?.question || "",
        item?.description || item?.value || item?.answer || "",
        item?.url || ""
      ];
      while (values.length > 1 && !values[values.length - 1]) {
        values.pop();
      }
      return values.join(separator);
    })
    .join("\n");
}

function parsePairs(value, keys, separator = "::") {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(separator).map((item) => item.trim());
      const item = {};
      keys.forEach((key, index) => {
        item[key] = parts[index] || "";
      });
      return item;
    });
}

function countFilled(values) {
  const total = values.length;
  const filled = values.filter((item) => String(item || "").trim().length > 0).length;
  return { filled, total };
}

function PreviewCard({ title, children }) {
  return (
    <div className="site-preview-card">
      <div className="site-preview-title">{title}</div>
      {children}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <label className="site-form-field">
      <span>{label}</span>
      <input value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder, hint, rows = 5 }) {
  return (
    <label className="site-form-field">
      <span>{label}</span>
      <textarea rows={rows} value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export default function AdminSiteEditor({
  config,
  loading,
  saving,
  dirty,
  tab,
  setTab,
  onReload,
  onSave,
  onChange,
  siteBaseUrl
}) {
  const home = config?.home || {};
  const hero = home.hero || {};
  const cta = home.cta || {};
  const contacts = home.contacts || {};
  const payment = config?.payment || {};
  const seo = config?.seo || {};

  const homeProgress = countFilled([
    hero.title,
    hero.subtitle,
    hero.primaryLabel,
    hero.primaryUrl,
    cta.title,
    cta.text
  ]);
  const paymentProgress = countFilled([
    payment.headline,
    payment.subheadline,
    payment.helpText,
    payment.agreementUrl
  ]);
  const seoProgress = countFilled([
    seo?.home?.title,
    seo?.home?.description,
    seo?.payment?.title,
    seo?.payment?.description
  ]);

  function update(path, value) {
    onChange((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      let cursor = next;
      for (let index = 0; index < path.length - 1; index += 1) {
        cursor[path[index]] = cursor[path[index]] || {};
        cursor = cursor[path[index]];
      }
      cursor[path[path.length - 1]] = value;
      return next;
    });
  }

  return (
    <section id="site-content" className="panel site-section">
      <div className="section-header">
        <div>
          <h2>Сайт и SEO</h2>
          <p className="section-description">
            Редактируйте главную, блок оплаты и мета-теги в одном месте. Формат специально упрощён для первого входа.
          </p>
        </div>
        <div className="section-header-actions">
          <button type="button" className="analytics-refresh" onClick={onReload} disabled={loading}>
            {loading ? "Загрузка..." : "Обновить"}
          </button>
          <button type="button" className="analytics-refresh" onClick={onSave} disabled={saving || !dirty}>
            {saving ? "Сохраняем..." : dirty ? "Сохранить" : "Сохранено"}
          </button>
        </div>
      </div>

      <div className="site-editor-topbar">
        <div className="site-tab-row">
          <button type="button" className={`site-tab ${tab === "home" ? "active" : ""}`} onClick={() => setTab("home")}>
            Главная · {homeProgress.filled}/{homeProgress.total}
          </button>
          <button type="button" className={`site-tab ${tab === "payment" ? "active" : ""}`} onClick={() => setTab("payment")}>
            Оплата · {paymentProgress.filled}/{paymentProgress.total}
          </button>
          <button type="button" className={`site-tab ${tab === "seo" ? "active" : ""}`} onClick={() => setTab("seo")}>
            SEO · {seoProgress.filled}/{seoProgress.total}
          </button>
        </div>
        <div className="site-status-line">
          <span>Публичный адрес: {siteBaseUrl}</span>
          <span>{dirty ? "Есть несохранённые изменения" : "Все изменения сохранены"}</span>
        </div>
      </div>

      <div className="site-editor-layout">
        <div className="site-form-column">
          {tab === "home" ? (
            <div className="site-form-group">
              <div className="site-group-title">1. Первый экран</div>
              <TextField label="Надзаголовок" value={hero.eyebrow} onChange={(value) => update(["home", "hero", "eyebrow"], value)} />
              <TextField label="Главный заголовок" value={hero.title} onChange={(value) => update(["home", "hero", "title"], value)} />
              <TextAreaField
                label="Подзаголовок"
                value={hero.subtitle}
                onChange={(value) => update(["home", "hero", "subtitle"], value)}
                rows={4}
              />
              <TextField label="Кнопка 1: текст" value={hero.primaryLabel} onChange={(value) => update(["home", "hero", "primaryLabel"], value)} />
              <TextField label="Кнопка 1: ссылка" value={hero.primaryUrl} onChange={(value) => update(["home", "hero", "primaryUrl"], value)} />
              <TextField label="Кнопка 2: текст" value={hero.secondaryLabel} onChange={(value) => update(["home", "hero", "secondaryLabel"], value)} />
              <TextField label="Кнопка 2: ссылка" value={hero.secondaryUrl} onChange={(value) => update(["home", "hero", "secondaryUrl"], value)} />
              <TextAreaField
                label="Подпись под кнопками"
                value={hero.footnote}
                onChange={(value) => update(["home", "hero", "footnote"], value)}
                rows={3}
              />
              <TextAreaField
                label="Бейджи на первом экране"
                value={(hero.badges || []).join("\n")}
                onChange={(value) => update(["home", "hero", "badges"], value.split("\n").map((item) => item.trim()).filter(Boolean))}
                hint="Одна строка = один бейдж"
                rows={4}
              />

              <div className="site-group-title">2. Содержательные блоки</div>
              <TextAreaField
                label="Преимущества"
                value={formatPairs(home.features)}
                onChange={(value) => update(["home", "features"], parsePairs(value, ["title", "description"]))}
                hint="Формат строки: Заголовок :: Описание"
              />
              <TextAreaField
                label="Сценарии использования"
                value={formatPairs(home.useCases)}
                onChange={(value) => update(["home", "useCases"], parsePairs(value, ["title", "description"]))}
                hint="Формат строки: Заголовок :: Описание"
              />
              <TextAreaField
                label="Шаги Как начать"
                value={formatPairs(home.steps)}
                onChange={(value) => update(["home", "steps"], parsePairs(value, ["title", "description"]))}
                hint="Формат строки: Заголовок :: Описание"
              />
              <TextAreaField
                label="FAQ"
                value={formatPairs(home.faq)}
                onChange={(value) => update(["home", "faq"], parsePairs(value, ["question", "answer"]))}
                hint="Формат строки: Вопрос :: Ответ"
                rows={6}
              />

              <div className="site-group-title">3. Финальный CTA и контакты</div>
              <TextField label="CTA: заголовок" value={cta.title} onChange={(value) => update(["home", "cta", "title"], value)} />
              <TextAreaField label="CTA: текст" value={cta.text} onChange={(value) => update(["home", "cta", "text"], value)} rows={3} />
              <TextField label="CTA: кнопка 1 текст" value={cta.primaryLabel} onChange={(value) => update(["home", "cta", "primaryLabel"], value)} />
              <TextField label="CTA: кнопка 1 ссылка" value={cta.primaryUrl} onChange={(value) => update(["home", "cta", "primaryUrl"], value)} />
              <TextField label="CTA: кнопка 2 текст" value={cta.secondaryLabel} onChange={(value) => update(["home", "cta", "secondaryLabel"], value)} />
              <TextField label="CTA: кнопка 2 ссылка" value={cta.secondaryUrl} onChange={(value) => update(["home", "cta", "secondaryUrl"], value)} />
              <TextField label="Контакты: заголовок" value={contacts.title} onChange={(value) => update(["home", "contacts", "title"], value)} />
              <TextAreaField label="Контакты: текст" value={contacts.text} onChange={(value) => update(["home", "contacts", "text"], value)} rows={3} />
              <TextAreaField
                label="Контакты и ссылки"
                value={formatPairs(contacts.items)}
                onChange={(value) => update(["home", "contacts", "items"], parsePairs(value, ["label", "value", "url"]))}
                hint="Формат строки: Название :: Значение :: Ссылка"
              />
            </div>
          ) : null}

          {tab === "payment" ? (
            <div className="site-form-group">
              <div className="site-group-title">1. Блок оплаты</div>
              <TextField label="Заголовок страницы оплаты" value={payment.headline} onChange={(value) => update(["payment", "headline"], value)} />
              <TextAreaField label="Подзаголовок" value={payment.subheadline} onChange={(value) => update(["payment", "subheadline"], value)} rows={4} />
              <TextAreaField label="Текст-подсказка" value={payment.helpText} onChange={(value) => update(["payment", "helpText"], value)} rows={4} />
              <TextAreaField label="Нижняя заметка" value={payment.note} onChange={(value) => update(["payment", "note"], value)} rows={3} />

              <div className="site-group-title">2. Ссылки и поддержка</div>
              <TextField label="Ссылка на пользовательское соглашение" value={payment.agreementUrl} onChange={(value) => update(["payment", "agreementUrl"], value)} />
              <TextField label="Ссылка для отключения автопродления" value={payment.cancelUrl} onChange={(value) => update(["payment", "cancelUrl"], value)} />
              <TextAreaField
                label="Контакты оплаты"
                value={formatPairs(payment.contacts)}
                onChange={(value) => update(["payment", "contacts"], parsePairs(value, ["label", "value", "url"]))}
                hint="Формат строки: Название :: Значение :: Ссылка"
              />
              <TextAreaField
                label="Ссылки после оплаты"
                value={formatPairs(payment.successLinks)}
                onChange={(value) => update(["payment", "successLinks"], parsePairs(value, ["label", "value", "url"]))}
                hint="Формат строки: Текст кнопки :: Доп.текст :: Ссылка"
              />
            </div>
          ) : null}

          {tab === "seo" ? (
            <div className="site-form-group">
              <div className="site-group-title">SEO главной</div>
              <TextField label="Title" value={seo?.home?.title} onChange={(value) => update(["seo", "home", "title"], value)} />
              <TextAreaField label="Description" value={seo?.home?.description} onChange={(value) => update(["seo", "home", "description"], value)} rows={3} />
              <TextField label="OG Title" value={seo?.home?.ogTitle} onChange={(value) => update(["seo", "home", "ogTitle"], value)} />
              <TextAreaField label="OG Description" value={seo?.home?.ogDescription} onChange={(value) => update(["seo", "home", "ogDescription"], value)} rows={3} />
              <TextField label="OG Image" value={seo?.home?.ogImage} onChange={(value) => update(["seo", "home", "ogImage"], value)} />
              <TextField label="Canonical URL" value={seo?.home?.canonicalUrl} onChange={(value) => update(["seo", "home", "canonicalUrl"], value)} />

              <div className="site-group-title">SEO оплаты</div>
              <TextField label="Title" value={seo?.payment?.title} onChange={(value) => update(["seo", "payment", "title"], value)} />
              <TextAreaField label="Description" value={seo?.payment?.description} onChange={(value) => update(["seo", "payment", "description"], value)} rows={3} />
              <TextField label="OG Title" value={seo?.payment?.ogTitle} onChange={(value) => update(["seo", "payment", "ogTitle"], value)} />
              <TextAreaField label="OG Description" value={seo?.payment?.ogDescription} onChange={(value) => update(["seo", "payment", "ogDescription"], value)} rows={3} />
              <TextField label="OG Image" value={seo?.payment?.ogImage} onChange={(value) => update(["seo", "payment", "ogImage"], value)} />
              <TextField label="Canonical URL" value={seo?.payment?.canonicalUrl} onChange={(value) => update(["seo", "payment", "canonicalUrl"], value)} />
            </div>
          ) : null}
        </div>

        <div className="site-preview-column">
          <PreviewCard title="Превью главной">
            <div className="site-preview-kicker">{hero.eyebrow || "Надзаголовок"}</div>
            <div className="site-preview-headline">{hero.title || "Главный заголовок"}</div>
            <p>{hero.subtitle || "Подзаголовок страницы"}</p>
            <div className="site-preview-chips">
              {(hero.badges || []).slice(0, 4).map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </div>
          </PreviewCard>

          <PreviewCard title="Превью оплаты">
            <div className="site-preview-headline">{payment.headline || "Заголовок оплаты"}</div>
            <p>{payment.subheadline || "Подзаголовок оплаты"}</p>
            <div className="site-preview-links">
              {(payment.contacts || []).slice(0, 3).map((item) => (
                <div key={`${item.label}-${item.value}`}>
                  <strong>{item.label}:</strong> {item.value || item.url || "—"}
                </div>
              ))}
            </div>
          </PreviewCard>

          <PreviewCard title="SEO-памятка">
            <p>Title: {seo?.home?.title || "—"}</p>
            <p>Description: {seo?.home?.description || "—"}</p>
            <p>Canonical: {seo?.home?.canonicalUrl || `${siteBaseUrl}/`}</p>
          </PreviewCard>
        </div>
      </div>
    </section>
  );
}
